/**
 * MarketingAgentPage — ИИ-агент маркетинга (оркестратор).
 *
 * Живой tool-loop (Claude Code) со стримингом: сайдбар чатов, потоковый вывод с таймером,
 * инструменты, стоп, очередь, впрыск, переключение модели, вложения (Ctrl+V), память.
 * Плюс панели «⚙️ Админ» (кабинеты/доступы) и «📚 База знаний» (курсы).
 */
import { useCallback, useEffect, useRef, useState } from "react";

const API = "/api/marketing";

type Chat = { id: number; title: string; model: string; started: number; pinned: number; running?: boolean; messages?: number };
type Ev = { type: string; text?: string; name?: string; input?: string; ms?: number; error?: string; elapsed_ms?: number; cost?: number; queue_left?: number };
type Msg = { id: number; role: string; username?: string; content: string; events?: Ev[] | null; created_at: string };
type Doc = { id: number; title: string; char_len: number };
type AdminAccount = { id: number; login: string; title?: string | null; manage_active: boolean; active_in_stats: number; note?: string | null };
type AdminStatus = { accounts: AdminAccount[]; model: string; courses: number; applied_total: number; applied_ok: number };
type CLine = { icon: string; text: string; think?: boolean };
type Live = { answer: string; console: CLine[]; thinking: boolean; writing: boolean };

function fmtMs(ms?: number): string {
  if (!ms) return "";
  return ms >= 1000 ? (ms / 1000).toFixed(1) + " с" : ms + " мс";
}
function eventsToConsole(events: Ev[] | null | undefined): CLine[] {
  const out: CLine[] = [];
  for (const e of events || []) {
    if (e.type === "tool") out.push({ icon: "🔧", text: `${e.name || "tool"}${e.input ? " · " + e.input : ""}` });
    else if (e.type === "think") out.push({ icon: "🧠", text: `думал ${fmtMs(e.ms)}`, think: true });
    else if (e.type === "note") out.push({ icon: "♻", text: e.text || "" });
    else if (e.type === "error") out.push({ icon: "✖", text: e.error || "ошибка" });
  }
  return out;
}

async function jget<T>(u: string): Promise<T> {
  const r = await fetch(u, { credentials: "include" });
  if (!r.ok) throw new Error(String(r.status));
  return r.json() as Promise<T>;
}
async function jpostForm(u: string, form: Record<string, string>): Promise<Response> {
  const fd = new URLSearchParams(form);
  return fetch(u, { method: "POST", credentials: "include", headers: { "Content-Type": "application/x-www-form-urlencoded" }, body: fd });
}

const MODELS = ["opus", "sonnet", "haiku"];

export function MarketingAgentPage() {
  const [chats, setChats] = useState<Chat[]>([]);
  const [activeId, setActiveId] = useState<number | null>(null);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [live, setLive] = useState<Live | null>(null);
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [input, setInput] = useState("");
  const [attachments, setAttachments] = useState<string[]>([]);
  const [queue, setQueue] = useState<{ id: number; message: string }[]>([]);
  const [withMemory, setWithMemory] = useState(false);
  const [showKb, setShowKb] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [docs, setDocs] = useState<Doc[]>([]);
  const [admin, setAdmin] = useState<AdminStatus | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const mediaRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const endRef = useRef<HTMLDivElement | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const startRef = useRef<number>(0);
  const activeRef = useRef<number | null>(null);
  activeRef.current = activeId;
  const subLiveRef = useRef<((cid: number) => void) | null>(null);

  const active = chats.find((c) => c.id === activeId) || null;

  const loadChats = useCallback(async () => {
    try {
      const cs = await jget<Chat[]>(`${API}/orch/chats`);
      setChats(cs);
      return cs;
    } catch { return [] as Chat[]; }
  }, []);

  const loadMessages = useCallback(async (cid: number) => {
    try { setMessages(await jget<Msg[]>(`${API}/orch/chats/${cid}/messages`)); } catch { /* */ }
  }, []);

  const loadQueue = useCallback(async (cid: number) => {
    try {
      const q = await jget<{ running: boolean; queue: { id: number; message: string }[] }>(`${API}/orch/chats/${cid}/queue`);
      setQueue(q.queue || []);
      return q.running;
    } catch { return false; }
  }, []);

  // таймер во время хода
  useEffect(() => {
    if (!running) return;
    const t = setInterval(() => setElapsed(Math.round((Date.now() - startRef.current) / 1000)), 500);
    return () => clearInterval(t);
  }, [running]);

  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, live]);

  const handleEvent = useCallback((ev: Ev) => {
    setLive((prev) => {
      const l: Live = prev || { answer: "", console: [], thinking: false, writing: false };
      switch (ev.type) {
        case "text": return { ...l, answer: l.answer + (ev.text || ""), writing: true };
        case "tool": return { ...l, writing: false, console: [...l.console, { icon: "🔧", text: `${ev.name || "tool"}${ev.input ? " · " + ev.input : ""}` }] };
        case "think_start": return { ...l, thinking: true, writing: false };
        case "think": return { ...l, thinking: false, console: [...l.console, { icon: "🧠", text: `думал ${fmtMs(ev.ms)}`, think: true }] };
        case "note": return { ...l, console: [...l.console, { icon: "♻", text: ev.text || "" }] };
        case "error": return { ...l, console: [...l.console, { icon: "✖", text: ev.error || "ошибка" }] };
        default: return l;
      }
    });
  }, []);

  const readStream = useCallback(async (bodyStream: ReadableStream<Uint8Array>, cid: number) => {
    startRef.current = Date.now();
    setRunning(true);
    setLive({ answer: "", console: [], thinking: false, writing: false });
    const reader = bodyStream.getReader();
    const dec = new TextDecoder();
    let buf = "";
    let queueLeft = 0;
    try {
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop() || "";
        for (const ln of lines) {
          if (!ln.trim()) continue;
          let ev: Ev;
          try { ev = JSON.parse(ln); } catch { continue; }
          if (ev.type === "done") { queueLeft = ev.queue_left || 0; }
          else handleEvent(ev);
        }
      }
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    setRunning(false);
    setLive(null);
    await loadMessages(cid);
    await loadQueue(cid);
    void loadChats();
    // Сервер уже запустил следующую задачу из очереди — подписываемся на её стрим.
    if (queueLeft > 0) setTimeout(() => subLiveRef.current?.(cid), 400);
  }, [handleEvent, loadMessages, loadQueue, loadChats]);

  const subscribeLive = useCallback(async (cid: number) => {
    try {
      const r = await fetch(`${API}/orch/chats/${cid}/live`, { credentials: "include" });
      if (r.ok && r.body) await readStream(r.body, cid);
    } catch { /* нет активного хода */ }
  }, [readStream]);
  subLiveRef.current = subscribeLive;

  const selectChat = useCallback(async (cid: number) => {
    setActiveId(cid);
    setLive(null);
    setRunning(false);
    await loadMessages(cid);
    const isRunning = await loadQueue(cid);
    if (isRunning) void subscribeLive(cid);
  }, [loadMessages, loadQueue, subscribeLive]);

  // первичная загрузка
  useEffect(() => {
    (async () => {
      const cs = await loadChats();
      if (cs.length) void selectChat(cs[0].id);
      else {
        const r = await fetch(`${API}/orch/chats`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/x-www-form-urlencoded" }, body: new URLSearchParams({ title: "Маркетинг", model: "opus" }) });
        if (r.ok) { const c = await r.json(); await loadChats(); void selectChat(c.id); }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const newChat = useCallback(async () => {
    const title = prompt("Название чата:", "Новый чат");
    if (title === null) return;
    const r = await fetch(`${API}/orch/chats`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/x-www-form-urlencoded" }, body: new URLSearchParams({ title: title || "Новый чат", model: "opus" }) });
    if (r.ok) { const c = await r.json(); await loadChats(); void selectChat(c.id); }
  }, [loadChats, selectChat]);

  const renameChat = useCallback(async (cid: number, cur: string) => {
    const title = prompt("Новое название:", cur);
    if (!title) return;
    await jpostForm(`${API}/orch/chats/${cid}/rename`, { title });
    void loadChats();
  }, [loadChats]);

  const deleteChat = useCallback(async (cid: number) => {
    if (!confirm("Удалить чат вместе с историей?")) return;
    await jpostForm(`${API}/orch/chats/${cid}/delete`, {});
    const cs = await loadChats();
    if (activeRef.current === cid) { if (cs[0]) void selectChat(cs[0].id); else { setActiveId(null); setMessages([]); } }
  }, [loadChats, selectChat]);

  const pinChat = useCallback(async (cid: number, pinned: number) => {
    await jpostForm(`${API}/orch/chats/${cid}/pin`, { pinned: String(pinned ? 0 : 1) });
    void loadChats();
  }, [loadChats]);

  const setModel = useCallback(async (cid: number, model: string) => {
    await jpostForm(`${API}/orch/chats/${cid}/model`, { model });
    void loadChats();
  }, [loadChats]);

  const send = useCallback(async () => {
    const cid = activeId;
    const text = input.trim();
    if (!cid || (!text && attachments.length === 0)) return;
    setErr(null);
    setInput("");
    const atts = attachments;
    setAttachments([]);
    // оптимистично покажем свой вопрос
    setMessages((m) => [...m, { id: -Date.now(), role: "user", content: text + (atts.length ? `\n📎 ${atts.length} файл(а)` : ""), created_at: "" }]);
    try {
      const r = await fetch(`${API}/orch/chats/${cid}/send`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, attachments: atts, with_memory: withMemory }),
      });
      const ct = r.headers.get("content-type") || "";
      if (ct.includes("application/json")) {
        const j = await r.json();
        if (j.queued) setErr(`Ход идёт — сообщение в очереди (#${j.position}).`);
        else if (j.injected) setErr("Передано ассистенту прямо в работу.");
        await loadQueue(cid);
        if (!running) void subscribeLive(cid);
      } else if (r.body) {
        await readStream(r.body, cid);
      }
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  }, [activeId, input, attachments, withMemory, running, loadQueue, subscribeLive, readStream]);

  const stop = useCallback(async () => {
    if (!activeId) return;
    await jpostForm(`${API}/orch/chats/${activeId}/stop`, {});
  }, [activeId]);

  const uploadFiles = useCallback(async (files: FileList | File[]) => {
    const arr = Array.from(files);
    if (!arr.length) return;
    const fd = new FormData();
    for (const f of arr) fd.append("files", f);
    try {
      const r = await fetch(`${API}/orch/upload`, { method: "POST", credentials: "include", body: fd });
      if (r.ok) { const j = await r.json(); setAttachments((a) => [...a, ...(j.files || [])]); }
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
  }, []);

  const onPaste = useCallback((e: React.ClipboardEvent) => {
    const files: File[] = [];
    for (const it of Array.from(e.clipboardData.items)) {
      if (it.kind === "file") { const f = it.getAsFile(); if (f) files.push(f); }
    }
    if (files.length) { e.preventDefault(); void uploadFiles(files); }
  }, [uploadFiles]);

  const startRecord = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        stream.getTracks().forEach((t) => t.stop());
        const fd = new FormData();
        fd.append("audio", blob, "voice.webm");
        fd.append("language", "ru");
        try {
          const r = await fetch(`${API}/orch/voice`, { method: "POST", credentials: "include", body: fd });
          const j = await r.json();
          if (j.text) setInput((s) => (s ? s + " " : "") + j.text);
          else if (j.detail) setErr(String(j.detail));
        } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);
    } catch (e) { setErr("Микрофон недоступен: " + (e instanceof Error ? e.message : String(e))); }
  }, []);

  const stopRecord = useCallback(() => {
    mediaRef.current?.stop();
    mediaRef.current = null;
    setRecording(false);
  }, []);

  const loadDocs = useCallback(async () => { try { setDocs(await jget<Doc[]>(`${API}/knowledge`)); } catch { /* */ } }, []);
  const loadAdmin = useCallback(async () => { try { setAdmin(await jget<AdminStatus>(`${API}/agent/admin/status`)); } catch { /* */ } }, []);

  return (
    <div style={{ display: "flex", gap: 12, height: "calc(100vh - 96px)", padding: "6px 2px" }}>
      {/* Сайдбар чатов */}
      <div style={{ width: 210, flexShrink: 0, display: "flex", flexDirection: "column", borderRight: "1px solid #eee", paddingRight: 8 }}>
        <button onClick={newChat} style={{ ...btn("#7e57c2"), marginBottom: 8 }}>＋ Новый чат</button>
        <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 3 }}>
          {chats.map((c) => (
            <div key={c.id} onClick={() => void selectChat(c.id)}
                 style={{ padding: "7px 9px", borderRadius: 8, cursor: "pointer", fontSize: 13,
                          background: c.id === activeId ? "#efe7fb" : "transparent",
                          display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontWeight: c.id === activeId ? 600 : 400 }}>
                {c.pinned ? "📌 " : ""}{c.title}
              </span>
              {c.running && <span style={{ width: 7, height: 7, borderRadius: "50%", background: "#188a42", flexShrink: 0 }} title="идёт ход" />}
            </div>
          ))}
        </div>
      </div>

      {/* Основная колонка */}
      <div style={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column" }}>
        {/* Тулбар */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
          <div style={{ fontSize: 15, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 260 }}>
            🧠 {active?.title || "ИИ-агент"}
          </div>
          {active && (
            <>
              <select value={active.model} onChange={(e) => void setModel(active.id, e.target.value)}
                      style={{ fontSize: 12, padding: "3px 6px", borderRadius: 6, border: "1px solid #d5d5d5" }}>
                {MODELS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
              <button onClick={() => renameChat(active.id, active.title)} style={btnMini()}>✏️</button>
              <button onClick={() => pinChat(active.id, active.pinned)} style={btnMini()}>{active.pinned ? "📌" : "📍"}</button>
              <button onClick={() => deleteChat(active.id)} style={btnMini()}>🗑</button>
            </>
          )}
          <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
            <button onClick={() => { const nv = !showAdmin; setShowAdmin(nv); if (nv) void loadAdmin(); }} style={btn(showAdmin ? "#5e35b1" : "#7e57c2")}><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 6, verticalAlign: -2 }}><path d="M4 7h8M16 7h4M4 17h4M12 17h8M14 4v6M8 14v6"/></svg>Админ</button>
            <button onClick={() => { const nv = !showKb; setShowKb(nv); if (nv) void loadDocs(); }} style={btn("#7e57c2")}>📚 База знаний ({docs.length})</button>
          </div>
        </div>

        {showAdmin && <AdminPanel admin={admin} reload={loadAdmin} />}
        {showKb && <KbPanel docs={docs} reload={loadDocs} setErr={setErr} />}

        {/* Лента */}
        <div style={{ flex: 1, overflowY: "auto", padding: "4px 2px", background: "#fafafa", borderRadius: 12, border: "1px solid #eee" }}>
          {messages.length === 0 && !live && (
            <div style={{ color: "#999", fontSize: 13, lineHeight: 1.6, padding: 18 }}>
              Живой агент с полным доступом к инструментам. Он сам читает данные Я.Директа, БД, курсы — и работает по шагам.
              Правки в кабинет предлагает на подтверждение. Примеры: «разбери просадку СПБ по данным и курсам», «собери отчёт за 30 дней с рекомендациями».
            </div>
          )}
          {messages.map((m) => <Bubble key={m.id} m={m} />)}
          {live && <LiveBubble live={live} elapsed={elapsed} />}
          <div ref={endRef} />
        </div>

        {/* Очередь */}
        {queue.length > 0 && (
          <div style={{ fontSize: 12, color: "#7e57c2", marginTop: 6 }}>
            В очереди: {queue.length} — {queue.map((q) => q.message.slice(0, 24)).join(" · ")}
          </div>
        )}
        {err && <div style={{ color: "#c0392b", fontSize: 12, marginTop: 6 }}>{err}</div>}

        {/* Вложения */}
        {attachments.length > 0 && (
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 6 }}>
            {attachments.map((a, i) => (
              <span key={i} style={{ fontSize: 11, background: "#efe7fb", padding: "2px 8px", borderRadius: 12 }}>
                📎 {a.split("/").pop()} <button onClick={() => setAttachments((x) => x.filter((_, j) => j !== i))} style={{ border: "none", background: "none", cursor: "pointer", color: "#c0392b" }}>✕</button>
              </span>
            ))}
          </div>
        )}

        {/* Ввод */}
        <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "flex-end" }}>
          <button onClick={() => fileRef.current?.click()} style={{ ...btnMini(), height: 42, width: 42, fontSize: 18 }} title="Прикрепить файл">📎</button>
          <input ref={fileRef} type="file" multiple style={{ display: "none" }} onChange={(e) => e.target.files && uploadFiles(e.target.files)} />
          <button onClick={recording ? stopRecord : startRecord}
                  style={{ ...btnMini(), height: 42, width: 42, fontSize: 18, background: recording ? "#c0392b" : "#f0f0f0", color: recording ? "#fff" : "#444" }}
                  title={recording ? "Остановить запись" : "Голосовой ввод"}>🎤</button>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onPaste={onPaste}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void send(); } }}
            placeholder={running ? "Идёт ход — можно дописать (уйдёт в работу или очередь)…" : "Задача агенту… (Enter — отправить, Ctrl+V — вставить скрин)"}
            rows={2}
            style={{ flex: 1, padding: "10px 12px", fontSize: 14, border: "1px solid #d5d5d5", borderRadius: 10, resize: "none", outline: "none", fontFamily: "inherit" }}
          />
          <label style={{ fontSize: 11, color: "#888", display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
            <input type="checkbox" checked={withMemory} onChange={(e) => setWithMemory(e.target.checked)} />
            память
          </label>
          {running
            ? <button onClick={stop} style={{ ...btn("#c0392b"), height: 42, padding: "0 18px" }}>⛔ Стоп</button>
            : <button onClick={() => void send()} disabled={!input.trim() && attachments.length === 0} style={{ ...btn("#7e57c2"), height: 42, padding: "0 20px", opacity: (!input.trim() && attachments.length === 0) ? 0.5 : 1 }}>Отправить</button>}
        </div>
      </div>
    </div>
  );
}

function Console({ lines, footer }: { lines: CLine[]; footer?: React.ReactNode }) {
  if (!lines.length && !footer) return null;
  return (
    <div style={{ padding: "9px 12px", background: "#10161d", color: "#cfe3b4", borderRadius: 10,
                  fontFamily: "ui-monospace,Menlo,Consolas,monospace", fontSize: 12.5, lineHeight: 1.55,
                  maxHeight: 300, overflowY: "auto" }}>
      {lines.map((l, i) => (
        <div key={i} style={{ color: l.think ? "#c9b8f5" : "#cfe3b4", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {l.icon} {l.text}
        </div>
      ))}
      {footer}
    </div>
  );
}

function Bubble({ m }: { m: Msg }) {
  const isUser = m.role === "user";
  const con = isUser ? [] : eventsToConsole(m.events);
  if (isUser) {
    return (
      <div style={{ display: "flex", justifyContent: "flex-end", padding: "6px 14px" }}>
        <div style={{ maxWidth: "78%", padding: "10px 14px", borderRadius: 14, fontSize: 14, lineHeight: 1.5, background: "#7e57c2", color: "#fff", whiteSpace: "pre-wrap" }}>{m.content}</div>
      </div>
    );
  }
  return (
    <div style={{ padding: "6px 14px" }}>
      {con.length > 0 && (
        <details style={{ marginBottom: 6 }}>
          <summary style={{ cursor: "pointer", color: "#7e57c2", fontSize: 12 }}>▸ что делал агент ({con.length} шаг.)</summary>
          <div style={{ marginTop: 5 }}><Console lines={con} /></div>
        </details>
      )}
      {m.content && (
        <div style={{ padding: "10px 14px", borderRadius: 14, fontSize: 14, lineHeight: 1.5, background: "#fff", border: "1px solid #e8e8e8" }}>
          <Markdown text={m.content} />
        </div>
      )}
    </div>
  );
}

function LiveBubble({ live, elapsed }: { live: Live; elapsed: number }) {
  const status = live.thinking ? "думает…" : (live.writing ? "пишет ответ…" : "работает…");
  const footer = (
    <div style={{ display: "flex", alignItems: "center", gap: 9, marginTop: live.console.length ? 6 : 0 }}>
      <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#a6e56b", flex: "0 0 auto", animation: "kbpulse 1s ease-in-out infinite" }} />
      <b style={{ color: "#a6e56b" }}>{status}</b>
      <span style={{ marginLeft: "auto", color: "#8fa3b3", fontVariantNumeric: "tabular-nums" }}>{elapsed} с</span>
    </div>
  );
  return (
    <div style={{ padding: "6px 14px" }}>
      <style>{"@keyframes kbpulse{0%,100%{opacity:1}50%{opacity:.3}}"}</style>
      <Console lines={live.console} footer={footer} />
      {live.answer && (
        <div style={{ marginTop: 8, padding: "10px 14px", borderRadius: 12, background: "#fff", border: "1px solid #e8e8e8", fontSize: 14, lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
          {live.answer}
        </div>
      )}
    </div>
  );
}

function AdminPanel({ admin, reload }: { admin: AdminStatus | null; reload: () => void }) {
  return (
    <div style={{ border: "1px solid #e0d4f2", background: "#f8f5fd", borderRadius: 12, padding: 12, marginBottom: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <strong style={{ fontSize: 13 }}>Панель управления</strong>
        {admin && <span style={{ fontSize: 11, color: "#7e57c2" }}>модель {admin.model} · курсов {admin.courses} · правок применено {admin.applied_ok}/{admin.applied_total}</span>}
        <button onClick={reload} style={{ ...btn("#e9e0f6", "#5e35b1"), marginLeft: "auto", padding: "4px 10px" }}>↻</button>
      </div>
      {admin?.accounts.map((a) => (
        <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", fontSize: 12, padding: "6px 10px", background: "#fff", borderRadius: 8, border: "1px solid #ece3f7", marginBottom: 4 }}>
          <span style={{ fontWeight: 600 }}>{a.title || a.login}</span>
          <span style={{ color: "#999" }}>login: {a.login}</span>
          <span style={{ color: "#666" }}>активных в статистике: {a.active_in_stats}</span>
          <span style={{ marginLeft: "auto", padding: "2px 10px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: a.manage_active ? "#e3f5e9" : "#fdecea", color: a.manage_active ? "#188a42" : "#c0392b" }}>
            {a.manage_active ? "✓ полный доступ" : "⚠ только отчёты"}
          </span>
          {a.note && <div style={{ width: "100%", fontSize: 11, color: "#c0392b" }}>{a.note}</div>}
        </div>
      ))}
      <div style={{ fontSize: 11, color: "#888", marginTop: 6 }}>«⚠ только отчёты» — кабинет ведёт агентство, правки живых кампаний недоступны, пока не подключён управляющий логин (<a href="/settings" style={{ color: "#7e57c2" }}>Настройки → «＋ Добавить кабинет»</a>).</div>
    </div>
  );
}

function KbPanel({ docs, reload, setErr }: { docs: Doc[]; reload: () => void; setErr: (s: string | null) => void }) {
  const ref = useRef<HTMLInputElement | null>(null);
  const [up, setUp] = useState(false);
  const upload = async (files: FileList | null) => {
    if (!files?.length) return;
    setUp(true); setErr(null);
    for (const f of Array.from(files)) {
      const fd = new FormData(); fd.append("file", f); fd.append("title", f.name);
      try {
        const r = await fetch(`${API}/knowledge/upload`, { method: "POST", credentials: "include", body: fd });
        if (!r.ok) { const t = await r.json().catch(() => ({})); throw new Error((t as { detail?: string }).detail || "ошибка"); }
      } catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    }
    setUp(false); if (ref.current) ref.current.value = ""; reload();
  };
  return (
    <div style={{ border: "1px solid #eadff5", background: "#faf7fe", borderRadius: 12, padding: 12, marginBottom: 10 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        <strong style={{ fontSize: 13 }}>Курсы по маркетингу ({docs.length})</strong>
        <label style={{ ...btn("#7e57c2"), marginLeft: "auto", cursor: up ? "wait" : "pointer" }}>
          {up ? "Загружаю…" : "＋ Загрузить"}
          <input ref={ref} type="file" multiple accept=".txt,.md,.csv,.json,.pdf,.docx" style={{ display: "none" }} onChange={(e) => upload(e.target.files)} />
        </label>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
        {docs.map((d) => (
          <span key={d.id} style={{ fontSize: 12, background: "#fff", border: "1px solid #efe7f9", borderRadius: 8, padding: "3px 8px" }}>
            {d.title} <span style={{ color: "#aaa" }}>{(d.char_len / 1000).toFixed(0)}к</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/** Мини-рендерер Markdown: заголовки, жирный, код, списки, таблицы. */
function Markdown({ text }: { text: string }) {
  const lines = (text || "").replace(/\r/g, "").split("\n");
  const blocks: React.ReactNode[] = [];
  let i = 0, key = 0;
  while (i < lines.length) {
    const line = lines[i];
    if (line.trim().startsWith("|") && i + 1 < lines.length && /^\s*\|?[\s:|-]+\|?\s*$/.test(lines[i + 1]) && lines[i + 1].includes("-")) {
      const header = splitRow(line);
      const rows: string[][] = [];
      i += 2;
      while (i < lines.length && lines[i].trim().startsWith("|")) { rows.push(splitRow(lines[i])); i++; }
      blocks.push(
        <div key={key++} style={{ overflowX: "auto", margin: "6px 0" }}>
          <table style={{ borderCollapse: "collapse", fontSize: 13, width: "100%" }}>
            <thead><tr>{header.map((h, j) => <th key={j} style={{ border: "1px solid #e2e2e2", padding: "4px 8px", background: "#f3eefa", textAlign: "left", whiteSpace: "nowrap" }}>{inline(h)}</th>)}</tr></thead>
            <tbody>{rows.map((r, ri) => <tr key={ri}>{r.map((cell, ci) => <td key={ci} style={{ border: "1px solid #eee", padding: "4px 8px" }}>{inline(cell)}</td>)}</tr>)}</tbody>
          </table>
        </div>);
      continue;
    }
    const h = /^(#{1,4})\s+(.*)$/.exec(line);
    if (h) { blocks.push(<div key={key++} style={{ fontWeight: 700, fontSize: h[1].length <= 2 ? 15 : 14, margin: "8px 0 4px" }}>{inline(h[2])}</div>); i++; continue; }
    if (/^\s*[-•*]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\s*[-•*]\s+/.test(lines[i])) { items.push(lines[i].replace(/^\s*[-•*]\s+/, "")); i++; }
      blocks.push(<ul key={key++} style={{ margin: "4px 0", paddingLeft: 20 }}>{items.map((it, j) => <li key={j}>{inline(it)}</li>)}</ul>);
      continue;
    }
    if (!line.trim()) { i++; continue; }
    blocks.push(<div key={key++} style={{ margin: "3px 0", whiteSpace: "pre-wrap" }}>{inline(line)}</div>);
    i++;
  }
  return <div>{blocks}</div>;
}
function splitRow(row: string): string[] { return row.trim().replace(/^\||\|$/g, "").split("|").map((s) => s.trim()); }
function inline(text: string): React.ReactNode {
  const parts: React.ReactNode[] = [];
  const re = /(\*\*[^*]+\*\*|`[^`]+`)/g;
  let last = 0, m: RegExpExecArray | null, k = 0;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith("**")) parts.push(<strong key={k++}>{tok.slice(2, -2)}</strong>);
    else parts.push(<code key={k++} style={{ background: "#f0ecf7", padding: "1px 4px", borderRadius: 4, fontSize: 12 }}>{tok.slice(1, -1)}</code>);
    last = m.index + tok.length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}
function btn(bg: string, color = "#fff"): React.CSSProperties {
  return { background: bg, color, border: "none", borderRadius: 8, padding: "7px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer" };
}
function btnMini(): React.CSSProperties {
  return { background: "#f0f0f0", color: "#444", border: "none", borderRadius: 6, padding: "5px 8px", fontSize: 13, cursor: "pointer" };
}
